/**
 * The 3D first run's showcase room and its camera tour.
 *
 * This is the living room from the 3D walk-through ad (Concept F in
 * [marketing/reels-scripts.md](../../marketing/reels-scripts.md)). The ad's
 * capture (`scripts/render3d-capture/`) and Remotion project
 * (`marketing/render3d-ad/`) read the room, the tour and the copy from
 * here, so the Reel a visitor saw and the room they land on can't drift.
 *
 * Built to look good in 3D: a big window on the wall the sofa sits under
 * (the sun is aimed through the largest window), two floor lamps flanking
 * the sofa for golden hour and night, a rug, walnut, cognac leather and
 * green velvet against the inferred oak floor and warm white walls.
 * `render3dShowcase.test.ts` checks fit, overlap, door swing, and that the
 * tour only walks where a person can stand.
 *
 * Coordinates are room inches, origin at the inside top-left corner, +y
 * down. At rotation 0 a piece's front (+y) faces down.
 */

import { presetForName, presetToFurniture, presetToMount, MOUNT_PRESETS } from "./catalog";
import { presetShape } from "./geom";
import type { TourScript } from "./render3d/tour";
import type { WalkShot } from "./render3d/types";
import type { Design, Furniture, MaterialId, Opening, WallMount } from "./types";

export const RENDER3D_STARTER_ROOM_ID = "starter_render3d";

/** 16'0" × 13'0", 9' ceiling. */
export const SHOWCASE_ROOM = { width: 192, depth: 156, ceiling: 108 } as const;

/** The wide window the sofa sits under (top wall, left → right). The sun comes in here. */
export const SHOWCASE_WINDOW: Opening = {
  id: "r3d_window",
  name: "Picture window",
  kind: "window",
  wallIndex: 0,
  alongWall: 96,
  width: 84,
  height: 58,
  fromFloor: 61,
};

/** A second window on the right wall (top → bottom) for side light. */
export const SHOWCASE_SIDE_WINDOW: Opening = {
  id: "r3d_window_side",
  name: "Window",
  kind: "window",
  wallIndex: 1,
  alongWall: 66,
  width: 40,
  height: 54,
  fromFloor: 59,
};

/** Door on the bottom wall (right → left): `alongWall: 32` centres it at x = 160. */
export const SHOWCASE_DOOR: Opening = {
  id: "r3d_door",
  name: "Door",
  kind: "door",
  wallIndex: 2,
  alongWall: 32,
  width: 36,
  height: 80,
  fromFloor: 40,
};

export type ShowcasePieceKey =
  | "rug"
  | "sofa"
  | "coffeeTable"
  | "armchairL"
  | "armchairR"
  | "lampL"
  | "lampR"
  | "plant"
  | "bookshelf";

export interface ShowcasePiece {
  key: ShowcasePieceKey;
  /** Catalog preset name. */
  preset: string;
  x: number;
  y: number;
  rot: number;
  material?: { id: MaterialId; tint: string };
}

export const SHOWCASE_PIECES: readonly ShowcasePiece[] = [
  // 8×10 rug turned to run along the window wall, under the seating group
  // and clear of the door's swing.
  { key: "rug", preset: "Area rug 8x10", x: 96, y: 72, rot: 90, material: { id: "wool-rug", tint: "#b7a58e" } },
  // Sofa under the window, facing the room.
  { key: "sofa", preset: "3-seat sofa", x: 96, y: 22, rot: 0, material: { id: "velvet", tint: "#365c45" } },
  { key: "coffeeTable", preset: "Coffee table", x: 96, y: 70, rot: 0, material: { id: "wood-dark", tint: "#6e574a" } },
  // A pair of armchairs facing each other across the table.
  { key: "armchairL", preset: "Armchair", x: 38, y: 78, rot: 270, material: { id: "leather", tint: "#9a6b45" } },
  { key: "armchairR", preset: "Armchair", x: 154, y: 78, rot: 90, material: { id: "leather", tint: "#9a6b45" } },
  // Floor lamps flanking the sofa: they glow for golden hour and night.
  { key: "lampL", preset: "Floor lamp", x: 36, y: 16, rot: 0 },
  { key: "lampR", preset: "Floor lamp", x: 156, y: 16, rot: 0 },
  { key: "plant", preset: "Plant (large)", x: 179, y: 14, rot: 0 },
  { key: "bookshelf", preset: "Bookshelf", x: 7, y: 128, rot: 270, material: { id: "wood-dark", tint: "#5c4a3f" } },
];

export const showcasePieceId = (key: ShowcasePieceKey) => `r3d_${key}`;

function placePiece(piece: ShowcasePiece): Furniture {
  const preset = presetForName(piece.preset);
  if (!preset) throw new Error(`Unknown preset: ${piece.preset}`);
  const base = presetToFurniture(preset, piece.x, piece.y);
  return {
    ...base,
    id: showcasePieceId(piece.key),
    rotation: ((piece.rot % 360) + 360) % 360,
    ...(piece.material ? { material: { ...piece.material }, color: piece.material.tint } : {}),
  };
}

function placeMount(id: string, presetName: string, wallIndex: number, alongWall: number, fromFloor: number): WallMount {
  const preset = MOUNT_PRESETS.find((m) => m.name === presetName);
  if (!preset) throw new Error(`Unknown mount preset: ${presetName}`);
  return { ...presetToMount(preset, wallIndex, alongWall), id, fromFloor };
}

/** The furnished living room the 3D first run lands on. */
export function buildRender3dShowcaseDesign(): Design {
  return {
    room: {
      id: RENDER3D_STARTER_ROOM_ID,
      name: "Our living room",
      points: presetShape("rectangle", SHOWCASE_ROOM.width, SHOWCASE_ROOM.depth),
      ceilingHeight: SHOWCASE_ROOM.ceiling,
      wallThickness: 5,
      shapeKind: "rectangle",
    },
    furniture: SHOWCASE_PIECES.map(placePiece),
    mounts: [
      // Left wall runs bottom → top: alongWall 78 centres the art at y = 78, over the left armchair.
      placeMount("r3d_art", "Art (large)", 3, 78, 62),
      // Right wall, between the window and the door.
      placeMount("r3d_art_small", "Art (medium)", 1, 122, 60),
    ],
    openings: [{ ...SHOWCASE_WINDOW }, { ...SHOWCASE_SIDE_WINDOW }, { ...SHOWCASE_DOOR }],
    person: { heightInches: 68, weightLbs: 160, position: null },
    unit: "imperial",
    consultations: [],
    rankings: [],
  };
}

/** True while the design is still the untouched 3D starter room. */
export function isRender3dStarterRoom(design: { room: { id: string } }): boolean {
  return design.room.id === RENDER3D_STARTER_ROOM_ID;
}

/** Heading toward a plan point, in the walk camera's convention (0 looks down the plan, clockwise). */
const yawToward = (from: { x: number; y: number }, to: { x: number; y: number }) =>
  Math.atan2(-(to.x - from.x), to.y - from.y);

/**
 * Just inside the door, looking left at the armchair and the art. The walk
 * steps in and pans right onto the sofa under the window: in a portrait
 * frame (about 50° across at eye level) that pan is the reveal; a landscape
 * frame sees the whole seating group by the end.
 */
export const TOUR_START: WalkShot = { x: 162, y: 138, yaw: yawToward({ x: 162, y: 138 }, { x: 40, y: 70 }), pitch: -0.05 };
/** A step in from the door, the sofa, both lamps and the window in view. */
export const TOUR_END: WalkShot = { x: 140, y: 118, yaw: yawToward({ x: 140, y: 118 }, { x: 102, y: 22 }), pitch: -0.12 };

/**
 * The landing's tour, and the ad's camera: the plan seen from above tilts
 * into 3D, the camera drops through the ceiling into the room, walks in,
 * then the light goes golden and then night. Captions are the landing's;
 * the ad bakes its own copy (`SHOWCASE_COPY`) over the same beats.
 */
export const SHOWCASE_TOUR: TourScript = {
  lighting: "day",
  segments: [
    {
      // Starts almost straight down and upright like the 2D plan (window
      // wall at the top), so the ad can cross-fade the drawn plan into it.
      kind: "orbit",
      ms: 2400,
      from: { yaw: Math.PI, elevation: 1.45, zoom: 1.04 },
      to: { yaw: TOUR_START.yaw, elevation: 0.9, zoom: 0.92 },
      caption: "Your floor plan, in 3D",
    },
    { kind: "descend", ms: 1600, caption: "Step inside" },
    { kind: "walk", ms: 4200, from: TOUR_START, to: TOUR_END, caption: "Walk it. Everything's to scale." },
    { kind: "hold", ms: 500, caption: "Walk it. Everything's to scale." },
    { kind: "hold", ms: 2200, lighting: "golden", caption: "Golden hour" },
    { kind: "hold", ms: 2200, lighting: "night", caption: "Night" },
    // Hand over in the warmest light, not in the dark.
    { kind: "hold", ms: 1200, lighting: "golden", caption: "Your turn" },
  ],
};

/**
 * Words the ad and the landing share. The landing's hero repeats the hook
 * of the ad that brought the visitor (`render3dCopyFor` in `landingCopy.ts`);
 * the Remotion project bakes the same lines into the Reel.
 */
export const SHOWCASE_COPY = {
  hooks: {
    pov: "POV: you're standing in the room you just drew.",
    fit: "Walk through your room before you buy the sofa.",
    light: "Same room. Noon. Golden hour. Midnight.",
    photo: "It's not a photo. It's your floor plan.",
  },
  beats: {
    plan: "Your floor plan…",
    inside: "…in 3D.",
    walk: "Walk it. Everything's to scale.",
    golden: "Golden hour.",
    night: "Midnight.",
    photo: "Not a photo.",
  },
  cta: "Step inside",
  ctaSub: "Free · No sign-in",
  end: {
    tagline: "Walk through your room before you buy.",
    cta: "Step inside",
    sub: "Free · No sign-in · roomcanvas.ai",
  },
} as const;
